import {FlatTreeControl} from '@angular/cdk/tree';
import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {DynamicDataSource} from './data-source.service';
import {SubSink} from '@Common/core/utils/subsink';
import {DataBaseTreeModel} from './data-base-tree.model';
import {ElementsCheckTreeModel} from '@Common/widgets/general/tree-lazy/elements-check.model';
import {MatCheckboxChange} from '@angular/material/checkbox';

/**
 * @title Tree data lazy loading, le data sont de type T, T doit au moins implémenter DataBaseTreeModel
 * Les parents sont chargés une 1ère fois via fctLoadParents
 * Les enfants d'un parent sont chargés au clic du parent dynamiquement via fctLoadChildren
 */
@Component({
  selector: 'p8-tree-lazy',
  templateUrl: './tree-lazy.component.html',
  styleUrls: ['./tree-lazy.component.scss'],
})
export class TreeLazyComponent<T extends DataBaseTreeModel> implements OnChanges {
  @Input() labelButton = 'Choisir';
  @Input() buttonChoice = false;
  /**
   * progress bar de chargement des enfants ?
   */
  @Input() showLoading = true;
  /**
   * Les champs à afficher dans la vue, par défaut "code"
   */
  @Input() fieldsDisplay = ['code'];
  /**
   * Service à utiliser pour fctLoadChildren ou fctLoadParents
   */
  @Input() set svcLoad(value) {
    this.dataSource.svcLoad = value;
  }
  /**
   * Fonction de chargement API parents / racine du service svcLoad
   * @private
   */
  @Input() set fctLoadParents(value) {
    this.dataSource.fctLoadParents = value;
  }
  /**
   * Argument pour l'appel de fctLoadParents, optionnel
   * @param {any | any[]} value : une valeur ou un tableau de valeurs à passer à la fonction, chaque valeur du tableau correspond à chaque argument de la fonction fctLoadChildren
   * @private
   */
  @Input() set argLoadParents(value) {
    this.dataSource.argLoadParents = value;
  }
  /**
   * Fonction de chargement API des enfants d'un père représenté par id du service svcLoad
   */
  @Input() set fctLoadChildren(value) {
    this.dataSource.fctLoadChildren = value;
  }
  /**
   * Argument supplémentaire pour l'appel de fctLoadParents, optionnel
   * @param {any | any[]} value : une valeur ou un tableau de valeurs à passer à la fonction, chaque valeur du tableau correspond à chaque argument de la fonction fctLoadChildren
   * @private
   */
  @Input() set argLoadChildren(value: any | any[]) {
    this.dataSource.argLoadChildren = value;
  }
  /**
   * Fonction de mapping modèle ramené par fctLoadParents ou fctLoadChildren en T[]
   */
  @Input() set mapModelFunction(value) {
    this.dataSource.mapModelFunction = value;
  }
  /**
   * Condition (optionnel) pour qu'un noeud père puisse être extensible (>), en plus de la nécessité d'avoir des enfants
   * @param {(T) => boolean} fct : la fonction à implémenter qui intégrera la condition
   */
  @Input() set conditionExpand(fct: (T) => boolean) {
    this.dataSource.conditionExpand = fct;
  }
  /**
   * Pour forcer le rechargement de l'arborscence, l'incrémenter le cas échéant
   */
  @Input() reload = 0;

  /**
   * Utiliser la sélection d'éléments ?
   */
  @Input() displayChecked = false;
  /**
   * Set des valeurs à cocher par défaut
   * @param {ElementsCheckTreeModel[]} values
   */
  @Input() set checkedElements(values: ElementsCheckTreeModel[]) {
    this._selectedSelection = values;
  }
  /**
   * A la sélection d'un élément de l'arbo
   */
  @Output() onSelect = new EventEmitter<T>();
  /**
   * Au check d'un élément : envoie toute la liste sélectionnée
   */
  @Output() onCheck = new EventEmitter<ElementsCheckTreeModel[]>();
  /**
   * Elément de l'arbo sélectionné
   */
  itemSelected: T;
  treeCodesControl: FlatTreeControl<T>;
  dataSource: DynamicDataSource<T>;
  /**
   * Pour la sélection des éléments
   */
  _selectedSelection: ElementsCheckTreeModel[] = [];
  subSink = new SubSink();
  constructor() {
    this.treeCodesControl = new FlatTreeControl<T>(this.getLevel, this.isExpandable);
    this.dataSource = new DynamicDataSource<T>(this.treeCodesControl);
  }
  getLevel = (node: T) => node.level;
  isExpandable = (node: T) => node.hasChildren;

  /**
   * Le noeud peut-il être étendu ?
   * @param _ : index noeud fils
   * @param {T} _nodeData : le noeud fils
   */
  canExpand(_: number, _nodeData: T) {
    if (_nodeData.expandable !== undefined) {
      return _nodeData.hasChildren && _nodeData.expandable;
    }
    return _nodeData.hasChildren;
  }

  /**
   * Gestion checkbox élement
   * @param {T} node
   * @private
   */
  private _findElementChecked(node: T): number {
    return this._selectedSelection.findIndex(_e => _e.id === node.id);
  }
  isSelected(node: T) {
    return this._findElementChecked(node) !== -1;
  }
  nodeSelectionToggle(event: MatCheckboxChange, node: T) {
    const element: ElementsCheckTreeModel = <ElementsCheckTreeModel> { id: node.id };
    const idx = this._findElementChecked(node);
    if (event.checked && idx === -1) {
      this._selectedSelection.push(element);
    }
    if (! event.checked && idx > -1) {
      this._selectedSelection.splice(idx, 1);
    }
    this.onCheck.emit(this._selectedSelection);
  }
  /**
   * Valeurs à afficher sur le noeud
   * @param item
   */
  getFieldsItemToDisplay(item) {
    if (this.fieldsDisplay && this.fieldsDisplay.length > 0) {
      return this.fieldsDisplay.reduce(function (acc, currentValue, idx) {
        if (idx === 0) {
          if (item[currentValue]) {
            return item[currentValue];
          }
          return '';
        } else {
          if (item[currentValue]) {
            return `${acc} (${item[currentValue]})`;
          } else {
            return acc;
          }
        }
      }, '');
    }
  }
  toolTipElement() {
    if (this.itemSelected) {
      return this.getFieldsItemToDisplay(this.itemSelected);
    }
    return '';
  }
  onSelectNode(node) {
    this.itemSelected = node;
    if (! this.buttonChoice) {
      this.onChoice();
    }
  }
  onChoice() {
    if (this.itemSelected) {
      this.onSelect.emit(this.itemSelected);
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.dataSource.initTree();
  }
}
